import * as React from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '../lib/utils.js';

// ─── Props ────────────────────────────────────────────────────

export interface MediaUnitLinkProps {
  /** Primary identifier displayed as the link text (e.g. "AMS-WAW-044"). */
  identifier: string;
  /** Optional secondary line below the identifier (e.g. city name). */
  subtitle?: string;
  /** Called when the user clicks or presses Enter on the link. No event argument — avoids unsafe casts. */
  onActivate?: () => void;
  /** Extra class names applied to the outer wrapper div. */
  className?: string;
  /** When true, renders the identifier with a line-through (e.g. SOLD units). */
  lineThrough?: boolean;
}

// ─── Component ───────────────────────────────────────────────

/**
 * Inline link to a media unit detail screen.
 * Shows an identifier + optional subtitle, with an ExternalLink icon
 * that fades in on hover. Both identifier and icon underline on hover.
 *
 * i18n: all display text comes from props — no hardcoded strings.
 */
export function MediaUnitLink({
  identifier,
  subtitle,
  onActivate,
  className,
  lineThrough = false,
}: MediaUnitLinkProps) {
  // Without an onActivate handler there is nothing to open, so the element must
  // not advertise itself as a link: no role/tab stop, no pointer cursor, no
  // hover underline or external-link icon. Rendering the affordance anyway
  // reads to users as "clicking does nothing".
  const interactive = onActivate !== undefined;

  return (
    <div
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      data-testid="media-unit-link"
      className={cn(
        'flex min-w-0 flex-col leading-tight',
        interactive && 'cursor-pointer group/unit',
        className,
      )}
      onClick={interactive ? () => onActivate?.() : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter') onActivate?.();
            }
          : undefined
      }
    >
      {/* Row flex container so the identifier text (a flex item) can be
          blockified and truncated with an ellipsis instead of overflowing
          into whatever sits to the right of a narrow column. */}
      <span
        className={cn(
          'flex min-w-0 items-center gap-1',
          'group-hover/unit:underline group-hover/unit:text-primary',
        )}
      >
        <span
          className={cn(
            // min-w-0 is required: flex items default to min-width:auto
            // (content-based), which silently defeats `truncate` otherwise.
            'min-w-0 flex-1 truncate text-[12px] font-medium text-foreground',
            lineThrough && 'line-through',
          )}
        >
          {identifier}
        </span>
        {interactive ? (
          <ExternalLink
            className="h-2.5 w-2.5 shrink-0 opacity-0 group-hover/unit:opacity-50"
            aria-hidden
          />
        ) : null}
      </span>
      {subtitle !== undefined ? (
        <span className="text-[10px] text-muted-foreground truncate">{subtitle}</span>
      ) : null}
    </div>
  );
}
